import { useCallback, useState } from "react";
import { Button } from "@/components/ui/button";
import type { Notification } from "@/lib/api/notifications";
import { respondToInvitation } from "@/lib/api/collaborators";
import { respondToTransfer } from "@/lib/api/transfers";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { getExternalLinkLabel } from "@/lib/utils/external-video-link";

const EMPTY_SET = new Set<string>();

interface NotificationActionsProps {
  notification: Notification;
  pendingInviteIds?: Set<string>;
  pendingTransferIds?: Set<string>;
  onActionComplete: () => void;
}

export function NotificationActions({
  notification,
  pendingInviteIds = EMPTY_SET,
  pendingTransferIds = EMPTY_SET,
  onActionComplete,
}: NotificationActionsProps) {
  const navigate = useNavigate();
  const { type, action_data } = notification;
  const [isLoading, setIsLoading] = useState(false);

  const handleNavigate = useCallback(
    (url: string) => {
      onActionComplete();
      // Navigate after dropdown closes to avoid unmount race
      setTimeout(() => navigate(url), 0);
    },
    [navigate, onActionComplete]
  );

  const handleCollabRespond = useCallback(
    async (accept: boolean) => {
      const inviteId = action_data?.collabInviteId as string | undefined;
      if (!inviteId) return;
      setIsLoading(true);
      try {
        await respondToInvitation(inviteId, accept);
        toast.success(
          accept
            ? "Invitation accepted! The class now appears on your profile."
            : "Invitation declined."
        );
        onActionComplete();
      } catch (err) {
        console.error("Failed to respond to collab invitation", err);
        toast.error("Failed to respond to invitation. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    [action_data, onActionComplete]
  );

  const primaryBtn =
    "h-7 text-[11px] font-semibold px-3.5 rounded-full shadow-sm";
  const secondaryBtn =
    "h-7 text-[11px] font-semibold px-3.5 rounded-full";

  switch (type) {
    case "collab_invite": {
      const inviteId = action_data?.collabInviteId as string | undefined;
      if (!inviteId || !pendingInviteIds.has(inviteId)) return null;
      return (
        <div className="flex gap-2 mt-2.5" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="default"
            className={primaryBtn}
            disabled={isLoading}
            onClick={() => handleCollabRespond(true)}
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              "ACCEPT"
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className={secondaryBtn}
            disabled={isLoading}
            onClick={() => handleCollabRespond(false)}
          >
            DECLINE
          </Button>
        </div>
      );
    }

    case "transfer_received": {
      const transferId = action_data?.transferId as string | undefined;
      if (!transferId || !pendingTransferIds.has(transferId)) return null;

      const handleTransferRespond = async (accept: boolean) => {
        setIsLoading(true);
        try {
          await respondToTransfer(transferId, accept ? "accept" : "decline");
          toast.success(accept ? "Transfer accepted!" : "Transfer declined.");
          onActionComplete();
        } catch (err) {
          console.error("Failed to respond to transfer", err);
          toast.error("Failed to respond to transfer. Please try again.");
        } finally {
          setIsLoading(false);
        }
      };

      return (
        <div className="flex gap-2 mt-2.5" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="default"
            className={primaryBtn}
            disabled={isLoading}
            onClick={() => handleTransferRespond(true)}
          >
            {isLoading ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              "ACCEPT"
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className={secondaryBtn}
            disabled={isLoading}
            onClick={() => handleTransferRespond(false)}
          >
            DECLINE
          </Button>
        </div>
      );
    }

    case "booking_approved":
    case "booking_denied":
    case "booking_changed":
    case "booking_cancelled":
      return (
        <div className="flex gap-2 mt-2.5" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="default"
            className={primaryBtn}
            onClick={() => handleNavigate(
              action_data?.bookingId
                ? `/bookings/${action_data.bookingId}`
                : "/rentals"
            )}
          >
            VIEW BOOKING
          </Button>
        </div>
      );

    case "waitlist_moved_off":
    case "payment_reminder":
      return (
        <div className="flex gap-2 mt-2.5" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="default"
            className={primaryBtn}
            onClick={() =>
              handleNavigate(`/classes/${action_data?.classId}?pay=1`)
            }
          >
            PAY
          </Button>
        </div>
      );

    case "videos_published": {
      const externalUrl = action_data?.externalVideoUrl as string | null;
      if (externalUrl) {
        const linkInfo = getExternalLinkLabel(externalUrl);
        return (
          <div className="flex gap-2 mt-2.5" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              variant="default"
              className={primaryBtn}
              onClick={() => {
                window.open(externalUrl, "_blank");
                onActionComplete();
              }}
            >
              {linkInfo.iconUrl ? (
                <img src={linkInfo.iconUrl} alt={linkInfo.label} className="h-3.5 w-3.5 mr-0.5" />
              ) : (
                <ExternalLink className="h-3 w-3 mr-0.5" />
              )}
              SEE VIDEOS
            </Button>
          </div>
        );
      }
      return (
        <div className="flex gap-2 mt-2.5" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="default"
            className={primaryBtn}
            onClick={() =>
              handleNavigate(`/classes/${action_data?.classId}/videos`)
            }
          >
            SEE VIDEOS
          </Button>
        </div>
      );
    }

    case "announcement":
      return (
        <div className="flex gap-2 mt-2.5" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="default"
            className={primaryBtn}
            onClick={() =>
              handleNavigate(`/classes/${action_data?.classId}#announcements`)
            }
          >
            VIEW
          </Button>
        </div>
      );

    case "staff_invitation":
      return (
        <div className="flex gap-2 mt-2.5" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="default"
            className={primaryBtn}
            onClick={() => {
              toast.success("Opening invitation...");
              if (action_data?.acceptUrl) {
                window.location.href = action_data.acceptUrl as string;
              }
            }}
          >
            ACCEPT
          </Button>
        </div>
      );

    case "gift_card_received":
      return (
        <div className="flex gap-2 mt-2.5" onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="default"
            className={primaryBtn}
            onClick={() => {
              toast.success("Opening gift card redemption...");
              handleNavigate("/memberships");
            }}
          >
            REDEEM
          </Button>
        </div>
      );

    default:
      return null;
  }
}
